import { useEffect, useState } from 'react'
import type { FormInstance } from 'antd'
import {
  getEdsByUserLogged,
  GetEmpresasByOrganizacion,
  GetEmpresasByRegionales,
  getRegionalesByNegocio,
} from '../services/ConfiguracionRecursos.service'
import { FIELDS_NAMES_RECURSO } from '../interfaces/FieldNamesRecurso'

export interface RegionalOption {
  label: string
  value: number
}

export interface OrganizacionOption {
  label: string
  value: number
}

export interface EmpresaOption {
  label: string
  value: number
  id_regional_negocio?: number
}

interface UseUbicacionEstacionesParams {
  form: FormInstance
  activo: boolean
  modoCreacion: boolean
  negocio: any
  empresasHijos: any
  organizacionesOptions: OrganizacionOption[]
}

const useUbicacionEstaciones = ({
  form,
  activo,
  modoCreacion,
  negocio,
  empresasHijos,
  organizacionesOptions,
}: UseUbicacionEstacionesParams) => {
  const [regionalesOptions, setRegionalesOptions] = useState<RegionalOption[]>([])
  const [loadingRegionales, setLoadingRegionales] = useState(false)

  const [empresasOptions, setEmpresasOptions] = useState<EmpresaOption[]>([])
  const [loadingEmpresas, setLoadingEmpresas] = useState(false)

  const [autoseleccionadas, setAutoseleccionadas] = useState(false)

  const cargarRegionales = () => {
    setLoadingRegionales(true)
    getRegionalesByNegocio(negocio)
      .then((data: any) => {
        const regionales: RegionalOption[] = (data ?? []).map((d: any) => ({
          value: d.id_regional,
          label: d.nombre_regional,
        }))
        setRegionalesOptions(regionales)
      })
      .catch(() => setRegionalesOptions([]))
      .finally(() => setLoadingRegionales(false))
  }

  const aplicarEmpresas = (empresas: EmpresaOption[], autoseleccionar: boolean) => {
    setEmpresasOptions(empresas)
    setAutoseleccionadas(autoseleccionar && empresas.length > 0)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.EMPRESAS]: autoseleccionar ? empresas.map((empresa) => empresa.value) : [],
    })
  }

  const cargarEmpresasBase = async () => {
    setLoadingEmpresas(true)
    try {
      const data: EmpresaOption[] = await getEdsByUserLogged(empresasHijos)
      aplicarEmpresas(data ?? [], false)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const cargarEmpresasPorRegionales = async (regionales: number[], organizacionId?: number | null) => {
    const organizaciones = organizacionId
      ? [organizacionId]
      : organizacionesOptions.map((organizacion) => organizacion.value)

    setLoadingEmpresas(true)
    try {
      const response = await GetEmpresasByRegionales(regionales, organizaciones)
      const empresas: EmpresaOption[] = (response.data?.body ?? []).map((e: any) => ({
        label: e.alias ?? e.razon_social,
        value: e.id,
        id_regional_negocio: e.id_regional_negocio,
      }))
      aplicarEmpresas(empresas, true)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const cargarEmpresasPorOrganizacion = async (organizacionId: number) => {
    setLoadingEmpresas(true)
    try {
      const response = await GetEmpresasByOrganizacion(organizacionId)
      const data = Array.isArray(response.data) ? response.data : (response.data?.data ?? response.data?.body ?? [])
      const empresas: EmpresaOption[] = (data ?? []).map((e: any) => ({
        label: e.razon_social ?? e.alias ?? '',
        value: Number(e.id),
      }))
      aplicarEmpresas(empresas, true)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const recargarEmpresas = (organizacionId: number | null, regionales: number[]) => {
    if (regionales.length > 0) return cargarEmpresasPorRegionales(regionales, organizacionId)
    if (organizacionId) return cargarEmpresasPorOrganizacion(organizacionId)
    return cargarEmpresasBase()
  }

  useEffect(() => {
    if (!activo || !modoCreacion) return
    cargarRegionales()
    cargarEmpresasBase()
  }, [activo, modoCreacion])

  const reset = () => {
    setRegionalesOptions([])
    setEmpresasOptions([])
    setAutoseleccionadas(false)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.ORGANIZACION]: null,
      [FIELDS_NAMES_RECURSO.REGIONALES]: [],
      [FIELDS_NAMES_RECURSO.EMPRESAS]: [],
    })
  }

  const fijarUbicacion = (estacion: EmpresaOption) => {
    setEmpresasOptions([estacion])
    setAutoseleccionadas(false)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.EMPRESAS]: [estacion.value],
      [FIELDS_NAMES_RECURSO.REGIONALES]: [],
      [FIELDS_NAMES_RECURSO.ORGANIZACION]: null,
    })
  }

  const onChangeOrganizacion = (organizacionId: number | null) => {
    form.setFieldsValue({ [FIELDS_NAMES_RECURSO.REGIONALES]: [] })
    recargarEmpresas(organizacionId ?? null, [])
  }

  const onChangeRegionales = (regionales: number[]) => {
    const organizacionId: number | null = form.getFieldValue(FIELDS_NAMES_RECURSO.ORGANIZACION) ?? null
    recargarEmpresas(organizacionId, regionales ?? [])
  }

  return {
    regionalesOptions,
    loadingRegionales,
    empresasOptions,
    loadingEmpresas,
    autoseleccionadas,
    onChangeOrganizacion,
    onChangeRegionales,
    fijarUbicacion,
    reset,
  }
}

export default useUbicacionEstaciones
